import axios from "axios";
import { useState, useEffect } from "react";
import { Roster, RosterMembership, RecruitWithPlayer } from "../types/RosterTypes";
import { API_BASE_URL } from "../constants";
import { DepthChart } from "../types/DepthChartTypes";
import { Season } from "../types/ScheduleTypes";

export function usePersonnel() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rosters, setRosters] = useState<Roster[]>([]);
  const [currentRoster, setCurrentRoster] = useState<Roster | null>(null);
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [currentSeason, setCurrentSeason] = useState<Season | null>(null);
  const [rosterPlayers, setRosterPlayers] = useState<RosterMembership[]>([]);
  const [recruits, setRecruits] = useState<RecruitWithPlayer[]>([]);

  // Fetch seasons on component mount
  useEffect(() => {
    fetchSeasons();
    fetchCurrentSeason();
    fetchCurrentRoster();
    fetchRecruits();
  }, []);
  
  const fetchSeasons = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/seasons/`);
      setSeasons(response.data);
      setIsLoading(false);
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.detail || err.message || "Error fetching seasons"
      );
      console.error("Error fetching seasons:", err);
    }
  };

  const fetchCurrentSeason = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/seasons/current/`);
      setCurrentSeason(response.data);
      setIsLoading(false);
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.detail ||
          err.message ||
          "Error fetching current season"
      );
      console.error("Error fetching current season:", err);
    }
  };

  const fetchRostersBySeason = async (seasonId: number) => {
    setIsLoading(true);
    setError(null);
    try {
      const url = `${API_BASE_URL}/rosters/?season=${seasonId}`;
      const response = await axios.get(url);
      setRosters(response.data);
      setIsLoading(false);
      return response.data;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.detail || err.message || "Error fetching rosters"
      );
      console.error("Error fetching rosters:", err);
      return [];
    }
  };

  const fetchRostersByYear = async (year: number) => {
    setIsLoading(true);
    setError(null);
    try {
      const url = `${API_BASE_URL}/rosters/?year=${year}`;
      const response = await axios.get(url);
      setRosters(response.data);
      setIsLoading(false);
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.detail || err.message || "Error fetching rosters"
      );
      console.error("Error fetching rosters:", err);
    }
  };

  const fetchCurrentRoster = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/rosters/current_roster/`
      );
      setCurrentRoster(response.data);
      setIsLoading(false);
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.detail ||
          err.message ||
          "Error fetching current roster"
      );
      console.error("Error fetching current roster:", err);
    }
  };

  const fetchRosterPlayers = async (rosterId: number) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/rosters/${rosterId}/roster_players/`
      );
      setRosterPlayers(response.data);
      setIsLoading(false);
      return response.data;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.detail ||
          err.message ||
          "Error fetching roster players"
      );
      console.error("Error fetching roster players:", err);
      return [];
    }
  };

  const removeRosterPlayer = async (rosterId: number, playerId: number) => {
    setError(null);
    setIsLoading(true);
    
    try {
      const response = await axios.post(
        `${API_BASE_URL}/roster-memberships/remove_player/`,  { roster_id: rosterId, player_id: playerId }
      );
      setIsLoading(false);
      return response.data;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.detail ||
          err.message ||
          "Error removing roster player"
      );
      console.error("Error removing roster player:", err);
      return null;
    }
  };

  // Fetch all depth charts
  const fetchDepthCharts = async (filters: Record<string, any> = {}) => {
    setIsLoading(true);
    setError(null);
    try {
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) queryParams.append(key, value.toString());
      });
      
      const response = await axios.get(`${API_BASE_URL}/depth-charts/?${queryParams}`);
      setIsLoading(false);
      return response.data;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.detail || err.message || "Error fetching depth charts"
      );
      console.error("Error fetching depth charts:", err);
      return [];
    }
  };

  // Fetch a specific depth chart
  const fetchDepthChart = async (id: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/depth-charts/${id}/`);
      setIsLoading(false);
      return response.data;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.detail || err.message || "Error fetching depth chart"
      );
      console.error("Error fetching depth chart:", err);
      return null;
    }
  };

  // Create a new depth chart
  const createDepthChart = async (depthChartData: DepthChart) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${API_BASE_URL}/depth-charts/`, depthChartData);
      setIsLoading(false);
      return response.data;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.detail || err.message || "Error creating depth chart"
      );
      console.error("Error creating depth chart:", err);
      return null;
    }
  };

  // Update a depth chart
  const updateDepthChart = async (id: string, depthChartData: DepthChart) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.put(`${API_BASE_URL}/depth-charts/${id}/`, depthChartData);
      setIsLoading(false);
      return response.data;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.detail || err.message || "Error updating depth chart"
      );
      console.error("Error updating depth chart:", err);
      return null;
    }
  };

  // Delete a depth chart
  const deleteDepthChart = async (id: string) => {
    setIsLoading(true);
    setError(null);
    try {
      await axios.delete(`${API_BASE_URL}/depth-charts/${id}/`);
      setIsLoading(false);
      return true;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.detail || err.message || "Error deleting depth chart"
      );
      console.error("Error deleting depth chart:", err);
      return false;
    }
  };

  // Helper to get the current season ID (default to 1 if not available)
  const getCurrentSeasonId = (): number => {
    return currentSeason?.id || 1; // Default to 1 if not available
  };

  // Update player information
  const updatePlayer = async (playerId: number, playerData: any) => {
    setError(null);
    setIsLoading(true);
    
    try {
      const response = await axios.patch(`${API_BASE_URL}/players/${playerId}/`, playerData);
      setIsLoading(false);
      return response.data;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.detail || err.message || "Failed to update player"
      );
      console.error("Error updating player:", err);
      return null;
    }
  };
  
  // Update roster membership (jersey number, status, etc.)
  const updateRosterMembership = async (membershipId: number, membershipData: any) => {
    setError(null);
    setIsLoading(true);
    
    try {
      const response = await axios.patch(`${API_BASE_URL}/roster-memberships/${membershipId}/`, membershipData);
      setIsLoading(false);
      return response.data;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.detail || err.message || "Failed to update roster membership"
      );
      console.error("Error updating roster membership:", err);
      return null;
    }
  };

  // Create or update a player and add them to a roster
  const createOrUpdatePlayer = async (playerData: any, rosterId: number | null) => {
    setError(null);
    setIsLoading(true);
    
    try {
      let result;
      
      // If no rosterId provided, use the current roster
      const effectiveRosterId = rosterId || (currentRoster?.id || null);
      
      if (!effectiveRosterId) {
        throw new Error("No roster selected. Please select a roster before adding a player.");
      }
      
      // If player has ID, update it
      if (playerData.id) {
        // Update existing player
        const updatedPlayer = await updatePlayer(playerData.id, playerData);
        
        // If we're updating an existing membership
        if (playerData.membershipId) {
          const updatedMembership = await updateRosterMembership(
            playerData.membershipId, 
            {
              jersey_number: playerData.jerseyNumber,
              status: playerData.status
            }
          );
          result = { player: updatedPlayer, membership: updatedMembership };
        } else {
          // Create a new membership for an existing player
          const membershipData = {
            player: playerData.id,
            roster: effectiveRosterId,
            jersey_number: playerData.jerseyNumber || null,
            status: playerData.status || "ACTIVE"
          };
          
          try {
            const membershipResponse = await axios.post(`${API_BASE_URL}/roster-memberships/`, membershipData);
            result = { player: updatedPlayer, membership: membershipResponse.data };
          } catch (membershipError) {
            console.error("Error creating membership for existing player:", membershipError);
            result = { player: updatedPlayer, membership: null };
          }
        }
      } else {
        // Create new player with roster membership in one request
        const createData = {
          ...playerData,
          roster_id: effectiveRosterId,  // Add roster_id for backend to handle
          // The jerseyNumber and status are already in playerData
        };
        
        console.log("Creating player with roster membership:", createData);
        
        const response = await axios.post(`${API_BASE_URL}/players/`, createData);
        result = {
          player: response.data,
          membership: response.data.membership || null
        };
      }
      
      // Refresh player data
      await fetchRosterPlayers(effectiveRosterId);
      
      setIsLoading(false);
      return result;
    } catch (err: any) {
      setIsLoading(false);
      const errorMsg = err.response?.data?.detail || err.message || "Failed to create or update player";
      setError(errorMsg);
      console.error("Error creating or updating player:", err);
      console.error("Error details:", err.response?.data || err.message);
      return null;
    }
  };

  const addRecruitToRoster = async (membershipData: any) => {
    const response = await axios.post(
      `${API_BASE_URL}/roster-memberships/`,
      membershipData
    );
    return response.data;
  };
  
  const fetchRecruits = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/recruits/with_players/`);
      setRecruits(response.data);
      setIsLoading(false);
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.detail ||
          err.message ||
          "Error fetching recruits"
      );
      console.error("Error fetching recruits:", err);
    }
  };

  // Update recruit information
  const createOrUpdateRecruit = async (recruitData: any) => {
    setError(null);
    setIsLoading(true);
    
    try {
      let response;
      
      // If recruit has an ID, update it; otherwise create a new one
      if (recruitData.id) {
        response = await axios.put(`${API_BASE_URL}/recruits/${recruitData.id}/`, recruitData);
      } else {
        response = await axios.post(`${API_BASE_URL}/recruits/`, recruitData);
      }
      
      // Refresh the recruits list
      await fetchRecruits();
      
      setIsLoading(false);
      return response.data;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.detail || err.message || "Failed to update recruit"
      );
      console.error("Error updating recruit:", err);
      return null;
    }
  };

  const archiveRecruit = async (recruitId: number) => {
    setError(null);
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API_BASE_URL}/recruits/${recruitId}/archive/`);
      
      // Refresh the recruits list
      await fetchRecruits();
      
      setIsLoading(false);
      return response.data;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.detail || err.message || "Failed to archive recruit"
      );
      console.error("Error archiving recruit:", err);
      return null;
    }
  };

  // Archive a player
  const archivePlayer = async (playerId: number) => {
    setError(null);
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API_BASE_URL}/players/archive/`, { player_id: playerId });
      
      // Refresh the roster players if we're currently viewing a roster
      if (currentRoster?.id) {
        await fetchRosterPlayers(currentRoster.id);
      }
      
      setIsLoading(false);
      return response.data;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.detail || err.message || "Failed to archive player"
      );
      console.error("Error archiving player:", err);
      return null;
    }
  };

  return {
    isLoading,
    error,
    seasons,
    currentSeason,
    rosters,
    currentRoster,
    rosterPlayers,
    recruits,
    fetchSeasons,
    fetchCurrentSeason,
    fetchRostersBySeason,
    fetchRostersByYear,
    fetchCurrentRoster,
    fetchRosterPlayers,
    fetchRecruits,
    createOrUpdateRecruit,
    archiveRecruit,
    addRecruitToRoster,
    // Depth chart methods
    fetchDepthCharts,
    fetchDepthChart,
    createDepthChart,
    updateDepthChart,
    deleteDepthChart,
    updatePlayer,
    updateRosterMembership,
    createOrUpdatePlayer,
    archivePlayer,
    removeRosterPlayer,
  };
}

export type PersonnelController = ReturnType<typeof usePersonnel>;
